import { Injectable, OnDestroy } from '@angular/core';
import { Observable, of, pipe, Subject, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, finalize, map, takeUntil } from 'rxjs/operators';
import { baseUrl } from '@environments/environment';
import { ToastService } from '@shared/services/toast/toast.service';
import { TranslateService } from '@ngx-translate/core';
import {
  RoutePlanningBase,
  CreateBasePayload,
  RoutePlanningRoute,
  CreateRoutePayload,
  RemoveBasePayload,
  RemoveRoutePayload,
  CreateBaseReportPayload,
  BaseReport,
  UpdateRoutePayload,
  UpdateBasePayload, RouteNote, RouteNotePayload, RouteNoteUpdatePayload, RouteNoteRemovePayload
} from "@shared/models/route-planning";

@Injectable({
  providedIn: 'root'
})
export class RoutePlanningService implements OnDestroy {

  componentDestroyed$: Subject<boolean> = new Subject()

  constructor(
    private http: HttpClient,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  getBases(projectId: number): Observable<RoutePlanningBase[]> {
    const url = `${baseUrl}/api/bases/${projectId}`;

    return this.http.get<RoutePlanningBase[]>(url)
    .pipe(
      map((response: any) => {
        return response['message'].map((base: any) => ({
          id: base.id,
          identifier: base.identifier,
          coordinates: JSON.parse(base.coordinates),
          notes: base.notes
        })) as RoutePlanningBase[];
      }),
      catchError((error) => this.handleError(error))
    );
  }

  createBase(data: CreateBasePayload): Observable<RoutePlanningBase> {
    if (!data) this.throwBadRequestError();
    const url = `${baseUrl}/api/bases/createBase`;

    return this.http.post<RoutePlanningBase>(url, data).pipe(
      map((response: any) => {
        return {
          id: response.message.id,
          coordinates: JSON.parse(response.message.coordinates),
          notes: response.message.notes,
          identifier: response.message.identifier,
        } as RoutePlanningBase;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  getRoutes(projectId: number): Observable<RoutePlanningRoute[]> {
    const url = `${baseUrl}/api/routes/${projectId}`;

    return this.http.get<RoutePlanningRoute[]>(url)
      .pipe(
        map((response: any) => {
          return response['message'].map((route: any) => ({
            id: route.id,
            identifier: route.identifier,
            coordinates: JSON.parse(route.coordinates),
            color: route.color,
            notes: route.notes,
          })) as RoutePlanningRoute[];
        }),
        catchError((error) => this.handleError(error))
      );
  }

  createRoute(data: CreateRoutePayload): Observable<RoutePlanningRoute> {
    if (data?.coordinates?.length <= 1) this.throwBadRequestError();
    const url = `${baseUrl}/api/routes/createRoute`;

    return this.http.post<RoutePlanningRoute>(url, data).pipe(
      map((response: any) => {
        return {
          id: response.message.id,
          coordinates: JSON.parse(response.message.coordinates),
          notes: response.message.notes,
          color: response.message.color,
          identifier: response.message.identifier,
        } as RoutePlanningRoute;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  updateRoute(data: UpdateRoutePayload): Observable<RoutePlanningRoute> {
    const url = `${baseUrl}/api/routes/updateRoute`;

    return this.http.post<RoutePlanningRoute>(url, data).pipe(
      map((response: any) => {
        return {
          id: response.message.id,
          coordinates: JSON.parse(response.message.coordinates),
          notes: response.message.notes,
          color: response.message.color,
          identifier: response.message.identifier,
        } as RoutePlanningRoute;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  updateBase(data: UpdateBasePayload): Observable<RoutePlanningBase> {
    const url = `${baseUrl}/api/bases/updateBase`;

    return this.http.post<RoutePlanningBase>(url, data).pipe(
      map((response: any) => {
        return {
          id: response.message.id,
          coordinates: JSON.parse(response.message.coordinates),
          notes: response.message.notes,
          identifier: response.message.identifier,
        } as RoutePlanningBase;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  removeBase(data: RemoveBasePayload): Observable<boolean> {
    if (!data) this.throwBadRequestError();
    const url = `${baseUrl}/api/bases/removeBase`;

    return this.http.post<boolean>(url, data).pipe(
      map((response: any) => {
        return response.message as boolean
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  removeRoute(data: RemoveRoutePayload): Observable<boolean> {
    if (!data) this.throwBadRequestError();
    const url = `${baseUrl}/api/routes/removeRoute`;

    return this.http.post<boolean>(url, data).pipe(
      map((response: any) => {
        return response.message as boolean
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  createBaseReport(data: CreateBaseReportPayload, formData: FormData): Observable<BaseReport> {
    if (!data) this.throwBadRequestError();

    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value as any);
    });

    const url = `${baseUrl}/api/baseReports/createBaseReport`;
    return this.http.post<BaseReport>(url, formData).pipe(
      map((response: any) => {
        return {
          id: response.message.id,
          base_id: response.message.base_id,
          status_id: response.message.status_id,
        } as BaseReport;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  getBaseReports(project_id: number): Observable<BaseReport[]> {
    const url = `${baseUrl}/api/baseReports/${project_id}`;

    return this.http.get<BaseReport[]>(url)
      .pipe(
        map((response: any) => {
          return response['message'] as BaseReport[];
        }),
        catchError((error) => this.handleError(error))
      );
  }

  getBaseReport(report_id: number): Observable<BaseReport> {
    const url = `${baseUrl}/api/baseReports/getBaseReport/${report_id}`;

    return this.http.get<BaseReport>(url)
      .pipe(
        map((response: any) => {
          return response['message'] as BaseReport;
        }),
        catchError((error) => this.handleError(error))
      );
  }

  getReportsForBase(base_id: number): Observable<BaseReport[]> {
    const url = `${baseUrl}/api/baseReports/getReportsForBase`;

    return this.http.post<BaseReport[]>(url, { base_id })
      .pipe(
        map((response: any) => {
          return response['message'] as BaseReport[];
        }),
        catchError((error) => this.handleError(error))
      );
  }

  getRouteNotes(projectId: number): Observable<RouteNote[]> {
    const url = `${baseUrl}/api/routes/getRouteNotes/${projectId}`;

    return this.http.get<RouteNote[]>(url)
      .pipe(
        map((response: any) => {
          return response['message'].map((route_note: any) => ({
            id: route_note.id,
            route_id: route_note.route_id,
            coordinates: JSON.parse(route_note.coordinates),
            notes: route_note.notes,
          })) as RouteNote[];
        }),
        catchError((error) => this.handleError(error))
      );
  }

  createRouteNote(data: RouteNotePayload): Observable<RouteNote> {
    if (!data?.coordinates?.lat || !data?.coordinates?.lng) this.throwBadRequestError();
    const url = `${baseUrl}/api/routes/createRouteNote`;

    return this.http.post<RouteNote>(url, data).pipe(
      map((response: any) => {
        return {
          id: response.message.id,
          coordinates: JSON.parse(response.message.coordinates),
          notes: response.message.notes,
        } as RouteNote;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  updateRouteNote(data: RouteNoteUpdatePayload): Observable<RouteNote> {
    const url = `${baseUrl}/api/routes/updateRouteNote`;

    return this.http.post<RouteNote>(url, data).pipe(
      map((response: any) => {
        return {
          id: response.message.id,
          coordinates: JSON.parse(response.message.coordinates),
          notes: response.message.notes,
        } as RouteNote;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  removeRouteNote(data: RouteNoteRemovePayload): Observable<boolean> {
    const url = `${baseUrl}/api/routes/removeRouteNote`;

    return this.http.post<boolean>(url, data).pipe(
      map((response: any) => {
        return response.message;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  private throwBadRequestError(): Observable<never> {
    return throwError(() => new HttpErrorResponse({
      error: { message: 'backend_errors.err_invalid_data' },
      status: 400,
      statusText: 'Bad Request'
    }));
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'backend_errors.err_unknown';

    if (error?.message) {
      errorMessage = error.error.message;
    }

    errorMessage = this.translateService.instant(`backend_errors.${errorMessage}`);
    this.toastService.sendToast(false, errorMessage)
    return throwError(() => new Error(errorMessage));
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true)
    this.componentDestroyed$.complete()
  }
}
